import type { ImageProps } from 'components/common/Image';
import { DUAL_BANNER_SLOTS } from 'components/hf/GlobalBanner/GlobalBanner.types';
import type { DualGlobalBannerData, GlobalBannerSlotContent, PrimaryMedia } from 'components/hf/GlobalBanner/GlobalBanner.types';

export const GLOBAL_BANNER_IMAGE_PRELOAD_TIMEOUT_MS = 5000;

interface PreloadOptions {
  imageLoader?: (image: ImageProps) => Promise<void>;
  matchesMedia?: (query: string) => boolean;
  timeoutMs?: number;
}

const defaultMatchesMedia = (query: string): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;

export const getGlobalBannerPreloadImage = (media: PrimaryMedia, matchesMedia: (query: string) => boolean = defaultMatchesMedia): ImageProps => {
  // Mirrors <picture>: use the first source whose media query matches; a source without media always matches.
  const source = media.sources?.find(candidate => !candidate.media || matchesMedia(candidate.media));
  return {
    src: media.src,
    ...(source?.srcset && { srcSet: source.srcset })
  };
};

const loadAndDecodeImage = (image: ImageProps): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    if (typeof image.src !== 'string' || !image.src) {
      resolve();
      return;
    }

    const element = document.createElement('img');
    element.onerror = () => reject(new Error('global banner image preload failed'));
    element.onload = () => {
      if (typeof element.decode === 'function') {
        Promise.resolve()
          .then(() => element.decode())
          .then(resolve, () => resolve());
      } else {
        resolve();
      }
    };

    if (typeof image.srcSet === 'string' && image.srcSet) {
      element.srcset = image.srcSet;
    }
    element.src = image.src;
  });

export const preloadDualGlobalBannerImages = (
  banners: DualGlobalBannerData,
  { imageLoader = loadAndDecodeImage, matchesMedia = defaultMatchesMedia, timeoutMs = GLOBAL_BANNER_IMAGE_PRELOAD_TIMEOUT_MS }: PreloadOptions = {}
): Promise<void> => {
  const slotBanners = DUAL_BANNER_SLOTS.map(slot => banners.slotData?.[slot]).filter((banner): banner is GlobalBannerSlotContent => Boolean(banner));

  if (slotBanners.length === 0) {
    return Promise.resolve();
  }
  if (slotBanners.some(banner => !banner.media?.primary?.src)) {
    return Promise.reject(new Error('global banner image source missing'));
  }

  const images = slotBanners.map(banner => getGlobalBannerPreloadImage(banner.media.primary, matchesMedia));

  const preload = Promise.all(images.map(image => imageLoader(image))).then(() => undefined);
  if (timeoutMs <= 0) {
    return preload;
  }

  return new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error('global banner image preload timed out')), timeoutMs);
    preload.then(
      () => {
        clearTimeout(timeout);
        resolve();
      },
      error => {
        clearTimeout(timeout);
        reject(error);
      }
    );
  });
};
